import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

interface Slide {
  id: string;
  badge: string;
  title: string;
  summary: string;
  image: string;
  to: string;
}

const SLIDES: Slide[] = [
  {
    id: "slide-1",
    badge: "剧情精选",
    title: "一场婚姻的裂痕与重建",
    summary:
      "十二年的婚姻像一间被雨水浸透的房子，外表完好，内里早已潮湿。当丈夫开始重新了解妻子，故事才真正开始。",
    image:
      "https://readdy.ai/api/search-image?query=cinematic%20moody%20film%20still%20of%20a%20dim%20modern%20apartment%20corridor%20at%20night%20with%20rain%20streaked%20tall%20window%2C%20deep%20navy%20and%20slate%20blue%20tones%2C%20single%20woman%20silhouette%20by%20the%20window%2C%20soft%20dramatic%20rim%20light%2C%20shallow%20depth%20of%20field%2C%20atmospheric%20haze%2C%20high%20detail%2C%20professional%20cinematography&width=1600&height=900&seq=iwm-hero-01&orientation=landscape",
    to: "/detail/drama-1",
  },
  {
    id: "slide-2",
    badge: "影视资讯",
    title: "大结局前夜，全网热议结局走向",
    summary:
      "收视率连续三周攀升，关于「素妍会选择和解还是放手」的讨论量突破百万，多个相关话题登上热搜榜。",
    image:
      "https://readdy.ai/api/search-image?query=cinematic%20editorial%20film%20still%20of%20a%20dark%20living%20room%20lit%20by%20a%20glowing%20television%20screen%2C%20deep%20navy%20and%20warm%20orange%20light%20spill%2C%20empty%20sofa%20and%20coffee%20table%2C%20intimate%20late%20night%20mood%2C%20atmospheric%20haze%2C%20high%20detail%2C%20professional%20cinematography&width=1600&height=900&seq=iwm-hero-02&orientation=landscape",
    to: "/detail/news-1",
  },
  {
    id: "slide-3",
    badge: "深度解读",
    title: "真相浮出水面的那一夜",
    summary:
      "第七集结尾的那通电话，是全剧的分水岭。一个被隐藏了十二年的秘密，在一场暴雨里被彻底揭开。",
    image:
      "https://readdy.ai/api/search-image?query=cinematic%20film%20noir%20still%20of%20a%20rainy%20city%20street%20at%20night%20with%20wet%20asphalt%20reflecting%20lamplight%2C%20deep%20navy%20and%20amber%20accent%20tones%2C%20lone%20figure%20with%20umbrella%20walking%20away%2C%20heavy%20rain%2C%20moody%20atmospheric%20haze%2C%20high%20detail%2C%20professional%20cinematography&width=1600&height=900&seq=iwm-hero-03&orientation=landscape",
    to: "/detail/drama-3",
  },
];

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback((index: number) => {
    setActive((index + SLIDES.length) % SLIDES.length);
  }, []);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % SLIDES.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [paused]);

  return (
    <section
      className="relative h-[460px] w-full overflow-hidden bg-primary-950 md:h-[600px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {SLIDES.map((slide, index) => (
        <div
          key={slide.id}
          className={[
            "absolute inset-0 transition-opacity duration-700",
            index === active ? "opacity-100" : "pointer-events-none opacity-0",
          ].join(" ")}
        >
          <img
            src={slide.image}
            alt={slide.title}
            title={`${slide.title} - 我妻子的一切`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/85 via-primary-950/60 to-primary-950/30" />
        </div>
      ))}

      <div className="relative z-10 flex h-full w-full items-center px-4 md:px-8">
        <div className="w-full max-w-2xl">
          <span className="inline-flex items-center rounded-md bg-accent-500 px-2.5 py-1 text-xs font-medium text-secondary-950">
            {SLIDES[active].badge}
          </span>
          <h2
            key={SLIDES[active].id}
            className="mt-4 animate-fade-up font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl"
          >
            {SLIDES[active].title}
          </h2>
          <p
            key={`${SLIDES[active].id}-summary`}
            className="mt-4 max-w-xl animate-fade-up text-sm leading-7 text-background-200 md:text-base"
          >
            {SLIDES[active].summary}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={SLIDES[active].to}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-secondary-950 transition-colors hover:bg-accent-400"
            >
              阅读全文
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/category/drama"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/60 px-5 py-2.5 text-sm text-background-100 transition-colors hover:bg-background-50/10"
            >
              <i className="ri-book-open-line" />
              浏览剧情档案
            </Link>
          </div>
        </div>
      </div>

      {/* arrows */}
      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(active - 1)}
        className="absolute left-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-background-300/40 bg-primary-950/40 text-background-100 backdrop-blur-sm transition-colors hover:bg-primary-950/70 md:left-6 md:h-10 md:w-10 cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(active + 1)}
        className="absolute right-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-background-300/40 bg-primary-950/40 text-background-100 backdrop-blur-sm transition-colors hover:bg-primary-950/70 md:right-6 md:h-10 md:w-10 cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      {/* tabs */}
      <div className="absolute bottom-6 left-4 right-4 z-20 hidden gap-3 md:left-8 md:flex">
        {SLIDES.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => go(index)}
            className="group flex flex-1 items-center gap-3 text-left cursor-pointer"
          >
            <span
              className={[
                "h-0.5 flex-1 rounded-full transition-colors",
                index === active ? "bg-accent-500" : "bg-background-400/40 group-hover:bg-background-300/70",
              ].join(" ")}
            />
            <span
              className={[
                "max-w-[180px] truncate text-xs transition-colors",
                index === active ? "text-background-50" : "text-background-400 group-hover:text-background-200",
              ].join(" ")}
            >
              {slide.title}
            </span>
          </button>
        ))}
      </div>

      {/* dots (mobile) */}
      <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 gap-2 md:hidden">
        {SLIDES.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            aria-label={`第 ${index + 1} 张`}
            onClick={() => go(index)}
            className={
              index === active
                ? "h-1.5 w-6 rounded-full bg-accent-500 transition-all"
                : "h-1.5 w-1.5 rounded-full bg-background-200/60 transition-all"
            }
          />
        ))}
      </div>
    </section>
  );
}